import React, { useContext, useMemo, useState, useEffect, useCallback, useLayoutEffect, useRef } from "react";
import type * as mapboxgl from "mapbox-gl";
import { useProjects } from "../api/projects";
import { useProjectsProperties } from "../api/properties";
import type { BoundaryStyle } from "../types/boundaryStyle";
import Map from "./Map";
import { Box, Button, Chip, Typography } from "@mui/material";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import { useNavigate } from "react-router-dom";
import { useSelection } from "../contexts/SelectionContext";
import { MapProvider, useMap } from "../contexts/MapContext";
import { DrawingContext, DrawingFeatureType, DrawingMode, DrawingProvider } from "../contexts/DrawingContext";
import MeasureLayer from "./layer_components/MeasureLayer";
import { LegendLayoutContext } from "./layer_components/legendSlots";
import type { LayerControlSide } from "./layerControl/layerControlSide";
import { Layer } from "../types/layer";
import type { ApiError } from "../types/api";
import type { Project } from "../types/project";
import ProjectLayersManager, { LayerState } from "./layer_components/ProjectLayersManager";
import RealtimeObservationsLayer from "./layer_components/RealtimeObservationsLayer";
import ProjectListingLayerPanel from "./layerControl/ProjectListingLayerPanel";
import { Filter } from "./layerControl/FilterModal";
import { RealtimeBbox, SimulatableKind } from "../api/realtimeObservations";
import SimulateDetectionDialog from "./SimulateDetectionDialog";
import CreateProjectTileSelector from "./layer_components/CreateProjectTileSelector";
import type { ProjectAreaPreview } from "./createProject/useProjectAreaPreview";
import { cameraFor, isGlobeSpanning } from "./projectListing/homeRegion";
import { type Bbox, bboxOfProjects } from "./projectListing/projectBbox";
import {
  type FlyToRequest,
  type FolderOverlay,
  type OpenFolderBoundary,
  useListingMapAnnotations,
} from "./projectListing/useListingMapAnnotations";
import type { StoryMarkerGroup } from "./projectListing/storyRows";
import { useListingStoryMarkers } from "./projectListing/useListingStoryMarkers";

interface ProjectsListingMapProps {
  searchQuery: string;
  tagFilter?: string;
}

type LatLng = { lat: number; lng: number };

/** A project being set up in the panel beside the map (see CreateProjectPane). */
export interface NewProjectOnMap {
  /** Its pin, tiles and property boundaries, plus the + buttons to add tiles. */
  area: ProjectAreaPreview;
  /** Set on the Location & Size step: a click on the map places the project there. */
  onPickLocation?: (latlng: LatLng) => void;
  hasLocation: boolean;
}

export interface ProjectListingMapContentProps {
  /** The projects to draw: the whole list (classic) or the open folder's. */
  projects: Project[];
  /** Projects hover/fly-to may name even when not drawn (hidden from the map). */
  lookupProjects?: Project[];
  /** The map's extent after each move. */
  onViewportChange?: (bounds: Bbox) => void;
  isLoading?: boolean;
  error?: ApiError | null;
  /**
   * Fill the parent instead of measuring to the viewport bottom, and keep the
   * map mounted when there is nothing to draw. The split view sets this; the
   * classic full-width map does not.
   */
  fillParent?: boolean;
  layerPanelPlacement?: "fixed" | "absolute";
  /** Edge of the map the layer pills hug (the split view puts them on the left). */
  layerPanelSide?: LayerControlSide;
  /** Hover from the table (instant): drawn as the highlight overlay. */
  hoveredProjectId?: string | null;
  /** A "show on map" click from the table: fly to this project. */
  flyTo?: FlyToRequest | null;
  /** Raw hover from the map; the page applies the dwell. */
  onHoverProject?: (id: string | null) => void;
  /** Sub-folders of the open folder, drawn as faint amber boxes and, when
   *  they have one, their boundary layer as well. */
  folders?: FolderOverlay[];
  /** The open folder's own boundary layer, drawn around everything in view. */
  openFolderBoundary?: OpenFolderBoundary | null;
  /** The folder whose row is hovered; its box is emphasised. */
  hoveredFolderId?: number | null;
  /** Raw hover over a folder box on the map. */
  onHoverFolder?: (id: number | null) => void;
  /** A click on a folder box (not on a project) opens it. */
  onOpenFolder?: (id: number) => void;
  /** Camera refit trigger: changes on folder/search, never on hover. */
  fitKey?: string;
  /** What to frame on refit: the open folder's full extent, or the search results. */
  fitBounds?: Bbox | null;
  /** Opening view, and the fallback when the projects would only fit as a globe. */
  homeBounds?: Bbox | null;
  /** Rendered over the map's top-right corner (the expand button); the
   *  top-left belongs to Mapbox's compass. */
  topRightSlot?: React.ReactNode;
  /** Fires once the map has loaded (tour ready gate). */
  onMapReady?: () => void;
  /** Mirrors whether a click-to-place mode is active. */
  onPlacementModeChange?: (active: boolean) => void;
  /** Folder a project created from the map's pick-location flow should land in. */
  createInFolderId?: number | null;
  /** The pick-location flow hands its spot here instead of opening the
   *  full-page wizard (the split view sets up the project beside the map). */
  onPickNewProjectLocation?: (latlng: LatLng) => void;
  /** A project being set up beside the map. While set, clicks on the map
   *  never open a project or folder. */
  newProject?: NewProjectOnMap | null;
  emptyMessage?: string;
  /** Off while the listing browses story maps: project areas are context there, not links. */
  projectClicksEnabled?: boolean;
  /** Off while browsing story maps: no "create a project here" button on the Project Areas pill. */
  createFromMapEnabled?: boolean;
  /** Story-map cover thumbnails, one marker per project or folder with stories (browse mode). */
  storyGroups?: StoryMarkerGroup[];
  /** Markers to emphasise: the groups whose rows are hovered in the table. */
  hoveredGroupKeys?: ReadonlySet<string>;
  onHoverStoryGroup?: (key: string | null) => void;
  /** A single-story marker was clicked. */
  onOpenStory?: (storyId: string) => void;
  /** A stacked marker was clicked: the page lists its stories to pick from. */
  onOpenStoryStack?: (group: StoryMarkerGroup, anchor: HTMLElement) => void;
}

const EMPTY_PROJECTS: Project[] = [];
const EMPTY_FOLDERS: FolderOverlay[] = [];
const EMPTY_GROUPS: StoryMarkerGroup[] = [];
const EMPTY_KEYS: ReadonlySet<string> = new Set();

export const ProjectListingMapContent: React.FC<ProjectListingMapContentProps> = ({
  projects,
  lookupProjects,
  onViewportChange,
  isLoading = false,
  error = null,
  fillParent = false,
  layerPanelPlacement = "fixed",
  layerPanelSide = "right",
  hoveredProjectId = null,
  flyTo = null,
  onHoverProject,
  folders = EMPTY_FOLDERS,
  openFolderBoundary = null,
  hoveredFolderId = null,
  onHoverFolder,
  onOpenFolder,
  fitKey,
  fitBounds = null,
  homeBounds = null,
  topRightSlot,
  onMapReady,
  onPlacementModeChange,
  createInFolderId = null,
  onPickNewProjectLocation,
  newProject = null,
  emptyMessage = "No projects in this folder",
  projectClicksEnabled = true,
  createFromMapEnabled = true,
  storyGroups = EMPTY_GROUPS,
  hoveredGroupKeys = EMPTY_KEYS,
  onHoverStoryGroup,
  onOpenStory,
  onOpenStoryStack,
}) => {
  const { selectedPolygonId, setSelectedPolygonId } = useSelection();
  const navigate = useNavigate();
  const { map } = useMap();
  // The distance tool: Map.tsx reads the drawing mode from DrawingContext and
  // collects clicks as line points while it is on. The provider sits around
  // this map (see the exports below), so the listing has its own tool state.
  const { drawingMode, setDrawingMode, setDrawingFeatureType } = useContext(DrawingContext);
  const measuring = drawingMode === DrawingMode.MEASURE;
  const measuringRef = useRef(measuring);
  measuringRef.current = measuring;
  const stopMeasuring = useCallback(() => {
    setDrawingMode(DrawingMode.NONE);
    setDrawingFeatureType(DrawingFeatureType.NONE);
  }, [setDrawingMode, setDrawingFeatureType]);
  const measureLayer = MeasureLayer("measure", "Measure");
  const [layers, setLayers] = useState<Layer[]>([]);
  const [layerStates, setLayerStates] = useState<Record<string, LayerState>>(
    {}
  );
  const [projectAreasVisible, setProjectAreasVisible] = useState(true);
  // The folder layer (boundaries and extent boxes) has its own eye, so
  // folders and project areas can be shown or hidden independently.
  const [foldersVisible, setFoldersVisible] = useState(true);
  const hasFolderShapes = folders.length > 0 || !!openFolderBoundary;
  // The drawn projects' property boundaries come with the project areas and
  // are switched on from the Project Areas panel — off by default (owner's
  // call): a listing full of cadastre parcels is noise until asked for.
  // Nothing is fetched while either is off.
  const [propertiesVisible, setPropertiesVisible] = useState(false);
  const propertyProjectIds = useMemo(() => projects.map((p) => p.id), [projects]);
  // Each project's own line style, for the layer's data-driven paint.
  const propertyStyles = useMemo(() => {
    // globalThis: this file imports the Map component under the same name.
    const styles = new globalThis.Map<string, BoundaryStyle>();
    projects.forEach((p) => {
      if (p.property_style) styles.set(String(p.id), p.property_style);
    });
    return styles;
  }, [projects]);
  const showProperties = projectAreasVisible && propertiesVisible;
  const { properties, isLoading: propertiesLoading } = useProjectsProperties(propertyProjectIds, showProperties);

  // Location-pick mode for "Create New Project": the next map click becomes the
  // new project's location. Mirrored into a ref so the click/navigation
  // handlers below can consult it without being rebuilt (and without
  // re-firing when the mode is cancelled).
  const [pickingLocation, setPickingLocation] = useState(false);
  const pickingLocationRef = useRef(false);
  useEffect(() => {
    pickingLocationRef.current = pickingLocation;
  }, [pickingLocation]);

  // Same click-to-place pattern for "Simulate detection": the next map click
  // becomes the simulated hotspot/lightning location, then a dialog collects
  // kind and lifespan.
  const [simulatingDetection, setSimulatingDetection] = useState(false);
  const simulatingDetectionRef = useRef(false);
  useEffect(() => {
    simulatingDetectionRef.current = simulatingDetection;
  }, [simulatingDetection]);
  const [simulateLatLng, setSimulateLatLng] = useState<{
    lat: number;
    lng: number;
  } | null>(null);
  // Bumped after a successful placement so the kind's layer switches itself
  // on — a detection landing on a hidden layer looks like nothing happened.
  const [activateSignals, setActivateSignals] = useState<
    Record<SimulatableKind, number>
  >({ hotspot: 0, lightning: 0 });

  // Setting up a new project beside the map: clicks belong to it, never to
  // opening a project or folder. Read from refs by the click handlers.
  const creatingRef = useRef(false);
  creatingRef.current = !!newProject;
  const pickNewProjectRef = useRef(newProject?.onPickLocation);
  pickNewProjectRef.current = newProject?.onPickLocation;
  const placingNewProject = !!newProject?.onPickLocation;
  const pickHereRef = useRef(onPickNewProjectLocation);
  pickHereRef.current = onPickNewProjectLocation;
  // A project clicked while setting one up was ignored. Forget it when the
  // set-up ends, or clicking that same project afterwards wouldn't open it.
  const creating = !!newProject;
  useEffect(() => {
    if (!creating) setSelectedPolygonId(null);
  }, [creating, setSelectedPolygonId]);

  // The split view's Escape key collapses the expanded map; it must not do
  // that while Escape is also how a placement mode is cancelled.
  const placementModeRef = useRef(onPlacementModeChange);
  placementModeRef.current = onPlacementModeChange;
  useEffect(() => {
    placementModeRef.current?.(pickingLocation || simulatingDetection);
  }, [pickingLocation, simulatingDetection]);

  // Tour ready gate: the first step points at a drawn map.
  const mapReadyRef = useRef(onMapReady);
  mapReadyRef.current = onMapReady;
  useEffect(() => {
    if (!map) return;
    const fire = () => mapReadyRef.current?.();
    if (map.loaded()) {
      fire();
      return;
    }
    map.once("load", fire);
    return () => {
      map.off("load", fire);
    };
  }, [map]);

  // Realtime layers read the live viewport (tenant-wide: shows whatever has
  // been ingested, no per-project gating on the listing map). The factory
  // re-reads this on debounced moveend.
  const getViewportBbox = useCallback(
    (map: mapboxgl.Map | null): RealtimeBbox | null => {
      if (!map) return null;
      const bounds = map.getBounds();
      if (!bounds) return null;
      return {
        top: bounds.getNorth(),
        bottom: bounds.getSouth(),
        left: bounds.getWest(),
        right: bounds.getEast(),
      };
    },
    []
  );
  const [hotspotsFilters, setHotspotsFilters] = useState<Filter[]>([]);
  const [lightningFilters, setLightningFilters] = useState<Filter[]>([]);
  const [hazardsFilters, setHazardsFilters] = useState<Filter[]>([]);
  const [ignitionPointsFilters, setIgnitionPointsFilters] = useState<Filter[]>([]);
  const [burntAreasFilters, setBurntAreasFilters] = useState<Filter[]>([]);

  // The classic listing page stacks tab and filter bars above this map, so a
  // fixed 100vh container hangs off the bottom of the page and takes the
  // bottom-anchored legend with it. Measure where the map actually starts and
  // claim only the remaining viewport height. The split view sizes the pane
  // itself, so it skips this.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerHeight, setContainerHeight] = useState("100vh");
  useLayoutEffect(() => {
    if (fillParent) return;
    const measure = () => {
      const top = containerRef.current?.getBoundingClientRect().top ?? 0;
      // Screen-pixel maths inside the zoomed UI (see index.css --ui-zoom).
      setContainerHeight(`calc((100vh - ${Math.round(top)}px) / var(--ui-zoom))`);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
    // Re-measure once the container actually mounts: this component returns a
    // loading/empty placeholder first, so the ref is null on the first pass.
  }, [isLoading, error, projects.length, fillParent]);
  const hotspotsLayer = RealtimeObservationsLayer({
    id: "realtime-hotspots",
    name: "Hotspots",
    kind: "hotspot",
    getBbox: getViewportBbox,
    available: true,
    filters: hotspotsFilters,
    scopeNote: "in this view (only areas of projects with detections on are collected)",
    activateSignal: activateSignals.hotspot,
  });
  const lightningLayer = RealtimeObservationsLayer({
    id: "realtime-lightning",
    name: "Lightning",
    kind: "lightning",
    getBbox: getViewportBbox,
    available: true,
    filters: lightningFilters,
    scopeNote: "in this view (only areas of projects with detections on are collected)",
    activateSignal: activateSignals.lightning,
  });
  const hazardsLayer = RealtimeObservationsLayer({
    id: "realtime-hazards",
    name: "Hazards incidents",
    kind: "hazard",
    getBbox: getViewportBbox,
    available: true,
    filters: hazardsFilters,
    // Hazard feeds are collected nationally, not per project, so unlike
    // hotspots/lightning there is no coverage caveat to explain here.
    scopeNote: "in this view",
  });
  const ignitionPointsLayer = RealtimeObservationsLayer({
    id: "realtime-ignition-points",
    name: "Ignition points",
    kind: "ignition",
    getBbox: getViewportBbox,
    available: true,
    filters: ignitionPointsFilters,
    scopeNote: "in this view (collected around this organisation's projects)",
  });
  const burntAreasLayer = RealtimeObservationsLayer({
    id: "realtime-burnt-areas",
    name: "Burnt areas",
    kind: "burnt_area",
    getBbox: getViewportBbox,
    available: true,
    filters: burntAreasFilters,
    scopeNote: "in this view (collected around this organisation's projects)",
  });
  const realtimeLayers = [
    hotspotsLayer,
    lightningLayer,
    hazardsLayer,
    ignitionPointsLayer,
    burntAreasLayer,
  ];

  const projectClicksRef = useRef(projectClicksEnabled);
  projectClicksRef.current = projectClicksEnabled;
  useEffect(() => {
    // While picking a location, a click on the map must not open a project.
    // Read the mode from the ref so leaving pick mode doesn't re-run this
    // effect and navigate on a stale selection.
    if (pickingLocationRef.current || simulatingDetectionRef.current || creatingRef.current) return;
    if (!projectClicksRef.current) return;
    if (selectedPolygonId) {
      const projectId = selectedPolygonId.split("-")[1];
      navigate(`/projects/${projectId}`);
    }
  }, [selectedPolygonId, navigate]);

  // In pick mode, listen on the map directly rather than going through the
  // layers' onClick: a click that lands on an existing project's polygon is
  // swallowed by that layer's handler, and picking a spot inside an existing
  // project has to work too.
  useEffect(() => {
    if (!pickingLocation || !map) return;

    const handlePick = (e: mapboxgl.MapMouseEvent) => {
      const latlng = { lat: e.lngLat.lat, lng: e.lngLat.lng };
      if (pickHereRef.current) {
        setPickingLocation(false);
        pickHereRef.current(latlng);
        return;
      }
      navigate("/create-project", {
        state: {
          selectedLatLng: latlng,
          folderId: createInFolderId,
        },
      });
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPickingLocation(false);
    };

    map.on("click", handlePick);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      map.off("click", handlePick);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [pickingLocation, map, navigate, createInFolderId]);

  // The new project's Location & Size step: a click places (or moves) it.
  // Also listened to on the map directly, so a spot inside an existing
  // project can be picked. The other click modes keep their clicks.
  useEffect(() => {
    if (!placingNewProject || !map) return;
    const handlePlace = (e: mapboxgl.MapMouseEvent) => {
      if (pickingLocationRef.current || simulatingDetectionRef.current || measuringRef.current) return;
      pickNewProjectRef.current?.({ lat: e.lngLat.lat, lng: e.lngLat.lng });
    };
    map.on("click", handlePlace);
    return () => {
      map.off("click", handlePlace);
    };
  }, [placingNewProject, map]);

  // Simulate-detection placement: identical shape to the pick-location mode
  // above (listen on the map directly so clicks over project polygons and
  // existing detections still place).
  useEffect(() => {
    if (!simulatingDetection || !map) return;

    const handlePlace = (e: mapboxgl.MapMouseEvent) => {
      setSimulateLatLng({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      setSimulatingDetection(false);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setSimulatingDetection(false);
    };

    map.on("click", handlePlace);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      map.off("click", handlePlace);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [simulatingDetection, map]);

  const handleProjectClick = useCallback(
    (projectId: string) => {
      if (pickingLocationRef.current || simulatingDetectionRef.current || creatingRef.current) return;
      if (!projectClicksRef.current) return;
      navigate(`/projects/${projectId}`);
    },
    [navigate]
  );

  // Story-map cover thumbnails (the listing's story-map browse mode).
  useListingStoryMarkers({
    map: (map as unknown as mapboxgl.Map | null) ?? null,
    groups: storyGroups,
    hoveredKeys: hoveredGroupKeys,
    onHover: onHoverStoryGroup,
    onOpenStory,
    onOpenStack: onOpenStoryStack,
  });

  // Hiding the project areas keeps the layers mounted and flips them to
  // inactive — Map drives Mapbox visibility off `active`, and it only garbage
  // collects orphaned `bbox-*` sources, not `project-*` ones.
  const projectLayers = useMemo(
    () =>
      projectAreasVisible
        ? layers
        : layers.map((layer) => ({ ...layer, active: false })),
    [layers, projectAreasVisible]
  );

  // Highlight, folder box, camera fit and hover reporting for the split view.
  // Harmless on the classic map: nothing is hovered and there is no fitKey.
  useListingMapAnnotations({
    map,
    projects,
    lookupProjects,
    onViewportChange,
    hoveredProjectId,
    flyTo,
    // The Folders eye governs every folder shape: sub-folder boxes and
    // boundaries, and the open folder's own boundary.
    folders: foldersVisible ? folders : EMPTY_FOLDERS,
    openFolderBoundary: foldersVisible ? openFolderBoundary : null,
    properties: showProperties ? properties : null,
    propertyStyles,
    hoveredFolderId,
    fitKey,
    fitBounds,
    homeBounds,
    onHoverProject,
    onHoverFolder,
    onOpenFolder,
    // Off while a click-to-place mode or the measure tool owns the pointer:
    // a click then adds a point, it must not open a folder.
    interactive: !!onHoverProject && !pickingLocation && !simulatingDetection && !measuring && !newProject,
  });

  // Initial camera: over the projects when they fit a region, else over the
  // home region (the user's country), so the first frame is never the whole
  // globe. Map's own fit (classic) or ours (split) takes over from there.
  const mapSettings = useMemo(() => {
    const b = bboxOfProjects(projects);
    const target = b && !(homeBounds && isGlobeSpanning(b)) ? b : homeBounds;
    if (!target) {
      return { centerPoint: { lat: 0, lng: 0 }, initialZoom: 1 };
    }
    const camera = cameraFor(target);
    return { centerPoint: camera.center, initialZoom: camera.zoom };
  }, [projects, homeBounds]);

  if (!fillParent) {
    if (isLoading) {
      return <div>Loading projects...</div>;
    }
    if (error) {
      return <div>Error loading projects: {error.message}</div>;
    }
    if (projects.length === 0) {
      return <div>No projects found.</div>;
    }
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "relative",
        height: fillParent ? "100%" : containerHeight,
        minHeight: 0,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        // Map re-sets the canvas cursor inline on every mousemove, so the
        // pick-mode crosshair has to be !important to survive.
        ...((pickingLocation || simulatingDetection || (placingNewProject && !measuring)) && {
          "& .mapboxgl-canvas": { cursor: "crosshair !important" },
        }),
      }}
    >
      <ProjectLayersManager
        projects={projects}
        onLayersUpdate={setLayers}
        layerStates={layerStates}
        setLayerStates={setLayerStates}
        onProjectClick={handleProjectClick}
      />

      {/* The realtime legends are the layers' customComponents, rendered inside
          Map; in the half-width pane they stack as chips rather than a row. */}
      <LegendLayoutContext.Provider value={fillParent ? "stacked" : "row"}>
        <Map
          layers={[...projectLayers, ...(newProject?.area.layers ?? []), measureLayer, ...realtimeLayers]}
          searchable={false}
          latlng={mapSettings.centerPoint}
          initialZoom={mapSettings.initialZoom}
          style={fillParent ? { height: "100%" } : undefined}
          autoFit={!fillParent}
        />
      </LegendLayoutContext.Provider>

      {newProject?.area.canExpand && newProject.area.tileGrid && (
        <CreateProjectTileSelector
          map={map}
          originLng={newProject.area.tileGrid.originLng}
          originLat={newProject.area.tileGrid.originLat}
          tileWidthDeg={newProject.area.tileGrid.tileWidthDeg}
          tileHeightDeg={newProject.area.tileGrid.tileHeightDeg}
          tiles={newProject.area.selectedTiles}
          onAdd={newProject.area.addTile}
          onRemove={newProject.area.removeTile}
        />
      )}

      {placingNewProject && !newProject?.hasLocation && !pickingLocation && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: "10px 16px",
            borderRadius: 2,
            backgroundColor: "rgba(30, 41, 59, 0.95)",
            backdropFilter: "blur(8px)",
            border: "2px solid rgba(148, 163, 184, 0.2)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
            pointerEvents: "none",
          }}
        >
          <AddLocationAltIcon sx={{ color: "#00d4ad" }} fontSize="small" />
          <Typography sx={{ color: "#f8fafc", fontSize: "0.875rem", fontWeight: 600 }}>
            Click on the map to place your new project
          </Typography>
        </Box>
      )}

      {topRightSlot && (
        <Box sx={{ position: "absolute", top: 10, right: 10, zIndex: 20 }}>{topRightSlot}</Box>
      )}

      {fillParent && !isLoading && !error && projects.length === 0 && !newProject && (
        <Chip
          label={emptyMessage}
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 20,
            backgroundColor: "rgba(30, 41, 59, 0.9)",
            color: "#f8fafc",
            fontWeight: 600,
          }}
        />
      )}
      {fillParent && error && (
        <Chip
          label={`Couldn't load projects: ${error.message}`}
          color="error"
          sx={{ position: "absolute", top: 16, left: "50%", transform: "translateX(-50%)", zIndex: 20 }}
        />
      )}

      {pickingLocation && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: "10px 16px",
            borderRadius: 2,
            backgroundColor: "rgba(30, 41, 59, 0.95)",
            backdropFilter: "blur(8px)",
            border: "2px solid rgba(148, 163, 184, 0.2)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
          }}
        >
          <AddLocationAltIcon sx={{ color: "#00d4ad" }} fontSize="small" />
          <Typography sx={{ color: "#f8fafc", fontSize: "0.875rem", fontWeight: 600 }}>
            Click on the map to place your new project
          </Typography>
          <Button
            size="small"
            onClick={() => setPickingLocation(false)}
            sx={{
              color: "#94a3b8",
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
              "&:hover": { backgroundColor: "rgba(148, 163, 184, 0.15)" },
            }}
          >
            Cancel (Esc)
          </Button>
        </Box>
      )}

      {simulatingDetection && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            padding: "10px 16px",
            borderRadius: 2,
            backgroundColor: "rgba(30, 41, 59, 0.95)",
            backdropFilter: "blur(8px)",
            border: "2px solid rgba(148, 163, 184, 0.2)",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.5)",
          }}
        >
          <AddLocationAltIcon sx={{ color: "#fbbf24" }} fontSize="small" />
          <Typography sx={{ color: "#f8fafc", fontSize: "0.875rem", fontWeight: 600 }}>
            Click on the map to place a simulated detection
          </Typography>
          <Button
            size="small"
            onClick={() => setSimulatingDetection(false)}
            sx={{
              color: "#94a3b8",
              textTransform: "none",
              fontSize: "0.75rem",
              fontWeight: 600,
              "&:hover": { backgroundColor: "rgba(148, 163, 184, 0.15)" },
            }}
          >
            Cancel (Esc)
          </Button>
        </Box>
      )}

      <SimulateDetectionDialog
        open={simulateLatLng !== null}
        latlng={simulateLatLng}
        onClose={() => setSimulateLatLng(null)}
        onCreated={(kind) =>
          setActivateSignals((prev) => ({ ...prev, [kind]: prev[kind] + 1 }))
        }
      />

      <ProjectListingLayerPanel
        placement={layerPanelPlacement}
        side={layerPanelSide}
        measureLayer={measureLayer}
        projectAreasVisible={projectAreasVisible}
        onToggleProjectAreas={() => setProjectAreasVisible((prev) => !prev)}
        projectCount={projects.length}
        propertiesVisible={propertiesVisible}
        onTogglePropertiesVisible={() => setPropertiesVisible((prev) => !prev)}
        propertyCount={properties?.features.length ?? 0}
        propertyProjectCount={properties?.project_count ?? 0}
        propertiesTruncated={(properties?.truncated_project_ids.length ?? 0) > 0}
        propertiesLoading={propertiesLoading}
        foldersVisible={foldersVisible}
        // The classic map has no folders at all, so it gets no pill.
        onToggleFolders={hasFolderShapes || onHoverFolder ? () => setFoldersVisible((prev) => !prev) : undefined}
        folderCount={folders.length + (openFolderBoundary ? 1 : 0)}
        folderBoundaryCount={folders.filter((f) => !!f.boundary).length + (openFolderBoundary ? 1 : 0)}
        pickingLocation={pickingLocation}
        onStartPickLocation={
          createFromMapEnabled
            ? () => {
                // The click-to-place modes and the measure tool are mutually
                // exclusive: one click, one meaning.
                stopMeasuring();
                setSimulatingDetection(false);
                setPickingLocation(true);
              }
            : undefined
        }
        onCancelPickLocation={() => setPickingLocation(false)}
        hotspotsLayer={hotspotsLayer}
        hotspotsFilters={hotspotsFilters}
        onHotspotsFiltersChange={setHotspotsFilters}
        lightningLayer={lightningLayer}
        lightningFilters={lightningFilters}
        onLightningFiltersChange={setLightningFilters}
        hazardsLayer={hazardsLayer}
        hazardsFilters={hazardsFilters}
        onHazardsFiltersChange={setHazardsFilters}
        ignitionPointsLayer={ignitionPointsLayer}
        ignitionPointsFilters={ignitionPointsFilters}
        onIgnitionPointsFiltersChange={setIgnitionPointsFilters}
        burntAreasLayer={burntAreasLayer}
        burntAreasFilters={burntAreasFilters}
        onBurntAreasFiltersChange={setBurntAreasFilters}
        simulatingDetection={simulatingDetection}
        onStartSimulateDetection={() => {
          // The two click-to-place modes (and the measure tool) are mutually exclusive.
          stopMeasuring();
          setPickingLocation(false);
          setSimulatingDetection(true);
        }}
        onCancelSimulateDetection={() => setSimulatingDetection(false)}
      />
    </Box>
  );
};

// The classic full-width listing map: fetches the list itself. The realtime
// layer factories (and the Map itself) talk through MapContext, which
// ProjectPage provides at page level — the listing page doesn't, so the
// provider is supplied here.
const ProjectsListingMap: React.FC<ProjectsListingMapProps> = ({ searchQuery, tagFilter }) => {
  const { projects, isLoading, error } = useProjects(searchQuery, tagFilter);
  return (
    <DrawingProvider>
      <MapProvider>
        <ProjectListingMapContent
          projects={projects?.data ?? EMPTY_PROJECTS}
          isLoading={isLoading}
          error={error ?? null}
        />
      </MapProvider>
    </DrawingProvider>
  );
};

export default ProjectsListingMap;
